import { createDraftSafeSelector as createSelector } from '@reduxjs/toolkit';

import { Inject } from '@vk-hr-tek/core/ioc';
import { UnitNodeLabeled } from '@vk-hr-tek/core/units';

import {
  CompanyPolicyListItem as CompanyPolicyListEntity,
  EmployeePolicyListItem as EmployeePolicyListEntity,
} from '@app/gen/policy';
import { UnitNode } from '@app/gen/settings';
import { RootState } from '@app/store';

import {
  CategoryMapper,
  CreationMapper,
  PolicyDetailMapper,
  PolicyMapper,
} from '../mappers';

const processUnitsTree = (node: UnitNode): UnitNodeLabeled => ({
  id: node.id,
  label: node.name,
  count: node.employee_count || 0,
  hasChildrenToLoad: node.is_parent,
  hasSeparateAbove: node.has_separate_above,
  isSeparate: node.is_separate,
  children: node.children
    ? node.children.map(processUnitsTree).filter((unit) => unit)
    : [],
});

export const selectPoliciesCompany = createSelector(
  [
    (state: RootState) => state.policy.currentIds,
    (state: RootState) => state.policy.entities,
    (_: RootState, inject: Inject) => inject(PolicyMapper),
  ],
  (currentIds, entities, format) =>
    currentIds
      .map((id) => entities[id])
      .filter((x): x is CompanyPolicyListEntity => x !== undefined)
      .map((policy) => format.processCompanyListEntity(policy)),
);

export const selectPoliciesEmployee = createSelector(
  [
    (state: RootState) => state.policy.currentIds,
    (_: RootState, inject: Inject, variant: 'desktop' | 'mobile' = 'desktop') =>
      variant,
    (state: RootState) => state.policy.entities,
    (_: RootState, inject: Inject) => inject(PolicyMapper),
  ],
  (currentIds, variant, entities, format) =>
    currentIds
      .map((id) => entities[id])
      .filter((x): x is EmployeePolicyListEntity => x !== undefined)
      .map((policy) => format.processEmployeeListEntity(policy, variant)),
);

export const selectIsNotLastItems = (state: RootState) =>
  state.policy.isNotLastItems;

export const selectFilters = createSelector(
  [
    (state: RootState) => state.policy.filters,
    (_: RootState, inject: Inject) => inject(PolicyMapper),
  ],
  (filters, format) => filters && format.processListFilters(filters),
);

export const selectError = (state: RootState) => state.policy.error;

export const selectStatus = (state: RootState) => state.policy.status;

export const selectCreationDataStatus = (state: RootState) =>
  state.policy.creationData.status;

export const selectCreationData = createSelector(
  [
    (state: RootState) => state.policy.creationData.companies,
    (state: RootState) => state.policy.creationData.status,
    (_: RootState, inject: Inject) => inject(CreationMapper),
  ],
  (companies, status, format) => ({
    companyList: companies
      ? format.processCreatePolicyCompany(companies)
      : null,
    companyListStatus: status,
  }),
);

export const selectCreationDataPositions = (state: RootState) =>
  state.policy.creationData.positions;

export const selectCreationDataEmployees = (state: RootState) =>
  state.policy.creationData.employees;

export const selectCreationDataUnitTypes = createSelector(
  [
    (state: RootState) => state.policy.creationData.unitTypes.values,
    (state: RootState) => state.policy.creationData.unitTypes.status,
  ],
  (values, status) => ({
    unitTypesOptions: values.types.map(({ name, type }) => ({
      value: type,
      label: name,
    })),
    unitTypesStatus: status,
  }),
);

export const selectCreationDataUnitsTree = createSelector(
  (state: RootState) => state.policy.creationData.unitsTree,
  (unitsTree) => {
    return {
      root_unit: unitsTree?.root_unit
        ? processUnitsTree(unitsTree.root_unit)
        : null,
    };
  },
);

export const selectGovDocumentOptions = createSelector(
  [
    (state: RootState) => state.policy.creationData.govDocumentOptions,
    (_: RootState, inject: Inject) => inject(CreationMapper),
  ],
  (docs, format) => (docs ? format.processGovDocumentOptions(docs) : []),
);

export const selectCategoriesOptions = createSelector(
  [
    (state: RootState) => state.policy.creationData.categories,
    (_: RootState, inject: Inject) => inject(CreationMapper),
  ],
  (categories, format) =>
    (categories || []).map((category) => format.processTree(category)),
);

export const selectPolicy = (state: RootState) => state.policy.detail.entity;

export const selectUnitTypes = (state: RootState) =>
  state.policy.detail.unitTypes;

export const selectPolicyEmployees = createSelector(
  [
    (state: RootState) => state.policy.detail.employees.entities,
    (_: RootState, inject: Inject) => inject(PolicyDetailMapper),
  ],
  (employees, format) => (employees ? format.processEmployees(employees) : []),
);

export const selectPolicyEmployeesHasMore = (state: RootState) =>
  state.policy.detail.employees.hasMore;

export const selectPolicyEmployeesStatus = (state: RootState) =>
  state.policy.detail.employees.status;

export const selectPolicyEmployeesError = (state: RootState) =>
  state.policy.detail.employees.error;

export const selectPolicyVersions = createSelector(
  [
    (state: RootState) => state.policy.detail.entity,
    (_: RootState, inject: Inject) => inject(PolicyDetailMapper),
  ],
  (entity, format) =>
    entity ? format.processVersions(entity.versions, entity.policy_id) : [],
);

export const selectPolicyDocVersionCode = (state: RootState) =>
  state.policy.detail.entity?.gov_document_type_code;

export const selectPolicyDocVersionName = (state: RootState) =>
  state.policy.detail.entity?.gov_document_type_name;

export const selectPolicyCategory = (state: RootState) => ({
  policyCategoryName: state.policy.detail.entity?.category_name,
  policyCategoryId: state.policy.detail.entity?.category_id,
});

export const selectActivePolicyVersion = createSelector(
  [
    (state: RootState) => state.policy.detail.entity,
    (_: RootState, inject: Inject) => inject(PolicyDetailMapper),
  ],
  (entity, format) => (entity ? format.findActiveVersion(entity.versions) : ''),
);

export const selectPrintPolicyStatus = (state: RootState) =>
  state.policy.printPolicy.status;

export const selectPoliciesTotalCount = (state: RootState) =>
  state.policy.totalCount;

export const selectPoliciesTotalUnacceptedCount = (state: RootState) =>
  state.policy.totalUnacceptedCount;

export const selectPolicyEditStatus = (state: RootState) =>
  state.policy.edit.status;

export const selectPolicyEditError = (state: RootState) =>
  state.policy.edit.error;

export const selectEditPolicyCompanies = createSelector(
  [
    (state: RootState) => state.policy.edit.companies.items,
    (state: RootState) => state.policy.edit.companies.status,
    (_: RootState, inject: Inject) => inject(CreationMapper),
  ],
  (companies, status, format) => ({
    companyList: companies
      ? format.processCreatePolicyCompany(companies)
      : null,
    companyListStatus: status,
  }),
);

export const selectEditPolicyCategories = createSelector(
  [
    (state: RootState) => state.policy.edit.categories,
    (_: RootState, inject: Inject) => inject(CreationMapper),
  ],
  (categories, format) =>
    (categories || []).map((category) => format.processTree(category)),
);

export const selectEditPolicyUnitsTrees = createSelector(
  (state: RootState) => state.policy.edit.unitsTrees,
  (unitsTrees) => {
    const mappedUnitsTrees: Record<
      string,
      { root_unit: UnitNodeLabeled } | null
    > = {};

    Object.entries(unitsTrees).forEach(([unitId, unitTree]) => {
      mappedUnitsTrees[unitId] = unitTree.root_unit
        ? { root_unit: processUnitsTree(unitTree.root_unit) }
        : null;
    });

    return mappedUnitsTrees;
  },
);

export const selectEditPolicyUnitTypes = createSelector(
  [
    (state: RootState) => state.policy.edit.unitTypes.values,
    (state: RootState) => state.policy.edit.unitTypes.status,
  ],
  (values, status) => {
    return {
      unitTypesOptions: values.types.map(({ type, name }) => ({
        value: type,
        label: name,
      })),
      unitTypesStatus: status,
    };
  },
);

export const selectEditPolicyUnitsTree = createSelector(
  (state: RootState) => state.policy.edit.unitsTree,
  (unitsTree) => {
    return {
      root_unit: unitsTree?.root_unit
        ? processUnitsTree(unitsTree.root_unit)
        : null,
    };
  },
);

export const selectCategoriesStatus = (state: RootState) =>
  state.policy.categories.status;

export const selectCategoriesError = (state: RootState) =>
  state.policy.categories.error;

export const selectCategoryAccounts = (state: RootState) =>
  state.policy.categories.creationData.accounts;

export const selectCategoryCreationDataAccountsStatus = (state: RootState) =>
  state.policy.categories.creationData.accountsStatus;

export const selectCategoryCreationDataCategoriesStatus = (state: RootState) =>
  state.policy.categories.creationData.categoriesStatus;

export const selectCategoryCreationDataCategories = createSelector(
  [
    (state: RootState) =>
      state.policy.categories.creationData.categoriesByAccountId,
    (_: RootState, inject: Inject) => inject(CategoryMapper),
    (_: RootState, __: Inject, accountId: string | undefined) => accountId,
  ],
  (categoriesByAccountId, mapper, accountId) => {
    if (!accountId) return [];

    const items = categoriesByAccountId[accountId];

    if (!items) return [];

    return mapper.processTreeViewOptions(categoriesByAccountId[accountId]);
  },
);

export const selectCategoryList = createSelector(
  [
    (state: RootState) => state.policy.categories.items,
    (_: RootState, inject: Inject) => inject(CategoryMapper),
    (_: RootState, __: Inject, expandedIds: Set<string>) => expandedIds,
  ],
  (items, mapper, expandedIds) =>
    mapper.processCategoryList(items, expandedIds),
);

export const selectCategoryDeleteStatus = (state: RootState) =>
  state.policy.categories.deleteStatus;

export const selectCategoriesTotal = (state: RootState) =>
  state.policy.categories.total;

export const selectCategoryParentIds = createSelector(
  [
    (state: RootState) => state.policy.categories.items,
    (_: RootState, inject: Inject) => inject(CategoryMapper),
    (_: RootState, __: Inject, createdCategoryId: string) => createdCategoryId,
  ],
  (items, mapper, createdCategoryId) => {
    if (!createdCategoryId) {
      return [];
    }

    const category = mapper.findNodeById(items, createdCategoryId);

    return category?.path ? category.path.slice(0, -1) : [];
  },
);
